import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from "react-router-dom";
import { Asset } from '../models/asset';
import { AssetData } from '../models/asset-data';
import { LookupItem } from '../models/lookup-item';
import { CrudFunctions } from '../utilities/crud-functions';
import DocumentLoader from './DocumentLoader';
import { UserDataStore } from '../store/userDataStore';
import { DocumentationConstants } from '../store/documentation-constants';
import AssetEditor from './AssetEditor';
import LogBookEntryComponent from './Common/LogBookEntryComponent';
import { PWDates } from '../utilities/pw-dates';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType, DocumentType } from '../models/enums';
import { ItemBasicInformation } from '../models/item-basic-information';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import DmaFooter from './DmaFooter';
import { ToastProps } from '../models/toast-props';

import DataGrid, {
    Column,
    FilterRow,
    GroupPanel,
    Selection,
    HeaderFilter,
    Scrolling,
    SearchPanel,
    Toolbar,
    Export,
    Item as ToolbarItem,
    Button as ColumnButton,
    FilterPanel,
    FilterBuilderPopup,
    Position,
    MasterDetail
} from 'devextreme-react/data-grid';

import TextBox from 'devextreme-react/text-box';
import SelectBox from 'devextreme-react/select-box';
import { Toast } from 'devextreme-react/toast';
import { saveAs } from 'file-saver';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';

import {
    Validator,
    RequiredRule,
    StringLengthRule
} from 'devextreme-react/validator';

import { Popup as DialogPopup } from 'devextreme-react/popup';
import { Button } from 'devextreme-react/button';
import { LoadPanel } from 'devextreme-react';

let key = 1;

export default function AssetsForm() {
    const [assets, setAssets] =
        useState<Asset[]>();

    const [selectedAsset, setSelectedAsset] =
        useState<Asset>();

    const [assetData, setAssetData] =
        useState<AssetData>();

    const [assetEditorVisible, setAssetEditorVisible] =
        useState<boolean>()

    const [newAssetNumber, setNewAssetNumber] =
        useState<string>();

    const [newAssetName, setNewAssetName] =
        useState<string>();

    const [newVmiGroup, setNewVmiGroup] =
        useState<LookupItem>();

    const [assetDocumentLoaderDialogVisible, setAssetDocumentLoaderDialogVisible] =
        useState<boolean>()

    const [logBookDialogVisible, setLogBookDialogVisible] =
        useState<boolean>()

    const [newAssetPromptVisible, setNewAssetPromptVisible] =
        useState<boolean>()

    const [editAssetId, setEditAssetId] =
        useState<string>();

    const [isModifyingChild, setIsModifyingChild] =
        useState<boolean>(false);

    const [isLoading, setIsLoading] =
        useState<boolean>(false);

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>()

    const parentGridRef = useRef(null);

    useEffect(() => {
        getAllData();
    }, []);

    useEffect(() => {
        setAssetEditorVisible(true);
    }, [editAssetId]);

    ++key;

    const user = UserDataStore.getCurrentUser();
    const canEditRoles = ["SA", "AS-A", "AS-L", "AS-T"];
    const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canInsertRoles = ["SA", "AS-A", "AS-L"];
    const canInsert = canInsertRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

    return (
        <div className="pw-content">
            <div className="lq-title-bar">
                <div className="lq-main-title">Assets</div>
                <div className="title-bar-spacer" />
                <div className="save-toolbar">
                    <div>
                        <NavLink to={`/documentation/${DocumentationConstants.AssetsForm}`}
                            className="pw-right-align-content" target="_blank" rel="noopener noreferrer">
                            <Button className="title-bar-button pw-orange"
                                text="" icon="help" type="normal"
                                hint="help and documentation" />
                        </NavLink>
                    </div>
                </div>
            </div>

            <div className="pw-margin-bottom-05">
                <LoadPanel visible={isLoading} />
                {/********** PARENT ASSETS ***********/}
                <DataGrid height="28rem" width="100%"
                    keyExpr="assetId"
                    ref={parentGridRef}
                    wordWrapEnabled={true}
                    dataSource={assets != null ? assets : []}
                    onSelectionChanged={assetGridSelectionChanged}
                    allowColumnResizing={true}
                    columnResizingMode={'widget'}
                    showRowLines={true}
                    rowAlternationEnabled={true}
                    onExporting={onExporting}
                    showBorders={true}>

                    <Selection mode={'single'} />
                    <HeaderFilter visible={true} />
                    <FilterRow visible={true} />
                    <GroupPanel visible={true} />
                    <SearchPanel visible={true} />
                    <Scrolling mode={'virtual'} />
                    <Export enabled={true} allowExportSelectedData={true} />
                    <FilterPanel visible={true} />
                    <FilterBuilderPopup>
                        <Position of={window} at='center' my='center' />
                    </FilterBuilderPopup>
                    <MasterDetail enabled={true} component={assetsDetailsPaneRender} />

                    <Column type="buttons" width="8rem">
                        <ColumnButton icon="edit" disabled={!canEdit}
                            onClick={onEditParentAssetClick} />
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={onAssetsAuditButtonClicked} />
                        <ColumnButton hint="information" icon="info"
                            onClick={showAssetInformationDialog} />
                        <ColumnButton icon="attach" hint="attach document"
                            onClick={onAssetEditDocuments}
                            render={renderDocumentsColumnButton} />
                        <ColumnButton icon="taskcomplete" hint="log book"
                            onClick={onShowLogbookDialogClick} />
                    </Column>

                    <Column dataField='formattedAssetNumber' caption="Asset#" dataType="string" width='6rem' />
                    <Column dataField='assetName' caption="Name" dataType="string" width='10rem' />

                    {/* <Column dataField='statusId' caption="Status" width='7rem' >
                        <Lookup dataSource={assetData && assetData.statuses ?
                            assetData.statuses : []}
                            allowClearing={true}
                            displayExpr='name' valueExpr='id' />
                    </Column>
                    */}

                    <Column dataField='vmiGroupName' dataType="string" width="12rem" groupIndex={0} />
                    <Column dataField='description' dataType="string" />
                    <Column dataField='vendor' dataType="string" width="10rem" />
                    <Column dataField='modelNumber' width='8rem' />
                    <Column dataField='vendorSerialNumber' caption="Vendor Serial#" width='9rem' />
                    <Column dataField='revalidationDate' width='7rem' dataType="date"
                        format="MM/dd/yyyy" cellRender={renderRevalidationCell} />

                    {/*
                    <Column dataField='validationDate' width='7rem' dataType="date"
                        format="MM/dd/yyyy" />
                    <Column dataField='revalidationDate' width='7rem' dataType="date"
                        format="MM/dd/yyyy" cellRender={renderRevalidationCell} />
                    <Column dataField='dateDisposed' width='7rem' dataType="date"
                        format="MM/dd/yyyy" />
                    <Column dataField='decommissionedDate' width='8rem' dataType="date"
                        format="MM/dd/yyyy" />
                    <Column dataField='decommissionedBy' width='10rem' dataType="string" />
                        */}

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Assets</div>
                            </div>
                        </ToolbarItem>

                        <ToolbarItem name="groupPanel" />
                        <ToolbarItem name="exportButton" />
                        <ToolbarItem name="searchPanel" />

                        <ToolbarItem location='after'>
                            <Button id="addNewAsset" type='normal' hint='Add New Asset' icon='plus'
                                width={'4em'} disabled={!canInsert}
                                onClick={onAddNewParentAssetClick} />
                        </ToolbarItem>
                    </Toolbar>

                </DataGrid>
            </div>

            <div className="pw-margin-bottom-05">
                {/********** CHILD ASSETS ***********/}
                <DataGrid height="20rem" width="100%"
                    keyExpr="assetId"
                    wordWrapEnabled={true}
                    dataSource={selectedAsset && selectedAsset.childAssets ? selectedAsset.childAssets : []}
                    allowColumnResizing={true}
                    columnResizingMode={'widget'}
                    showRowLines={true}
                    rowAlternationEnabled={true}
                    onExporting={onExporting}
                    showBorders={true}>

                    <Selection mode='single' />
                    <Scrolling mode='virtual' />
                    <MasterDetail enabled={true} component={assetsDetailsPaneRender} />

                    <Column type="buttons">
                        <ColumnButton icon="edit" disabled={!canEdit}
                            onClick={onEditChildAssetClick} />
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={onAssetsAuditButtonClicked} />
                        <ColumnButton hint="information" icon="info"
                            onClick={showAssetInformationDialog} />
                        <ColumnButton icon="attach" hint="attach document"
                            onClick={onAssetEditDocuments}
                            render={renderDocumentsColumnButton} />
                    </Column>

                    <Column dataField='formattedAssetNumber' caption="Asset#" dataType="string" width='6rem' />
                    <Column dataField='assetName' caption="Name" dataType="string" width='10rem' />
                    <Column dataField='vmiGroupName' dataType="string" width="12rem" groupIndex={0} />
                    <Column dataField='description' dataType="string" />
                    <Column dataField='vendor' dataType="string" width="10rem" />
                    <Column dataField='modelNumber' width='8rem' />
                    <Column dataField='vendorSerialNumber' caption="Vendor Serial#" width='9rem' />
                    <Column dataField='revalidationDate' width='7rem' dataType="date"
                        format="MM/dd/yyyy" cellRender={renderRevalidationCell} />

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Child Assets</div>
                            </div>
                        </ToolbarItem>

                        <ToolbarItem location='after'>
                            <Button id="addNewAsset" type='normal' hint='Add New Child Asset' icon='plus'
                                width={'4em'} disabled={!canInsert}
                                onClick={onAddNewChildAssetClick} />
                        </ToolbarItem>
                    </Toolbar>

                </DataGrid>
            </div>

            <DmaFooter />

            {
                editAssetId && assetEditorVisible &&
                <DialogPopup
                    visible={assetEditorVisible}
                    onHiding={hideAssetEditor}
                    fullScreen={true}
                    dragEnabled={false} hideOnOutsideClick={false}
                    showTitle={true} title="Asset Editor">
                    <AssetEditor assetId={editAssetId}
                        isReadOnly={!canEdit}
                        isChildAsset={isModifyingChild}
                        technologies={selectedAsset?.technologies ?? []}
                        assetData={assetData}
                        onClose={hideAssetEditor} />
                </DialogPopup>
            }

            {
                newAssetPromptVisible &&
                <DialogPopup
                    visible={newAssetPromptVisible}
                    onHiding={hideNewAssetPrompt}
                    onShowing={onShowingNewAssetPrompt}
                    dragEnabled={false} hideOnOutsideClick={false}
                    showTitle={true} title="Add New Asset"
                    width={400} height={350}>

                    <div className="form-unit">
                        <label className="textbox-label">Name</label>
                        <TextBox id="txtNewAssetName"
                            className="textbox"
                            onValueChanged={onNewAssetNameChanged}
                            placeholder="name" value={newAssetName} >
                            <RequiredRule message="Name is required." />
                        </TextBox>
                    </div>

                    {!isModifyingChild &&
                        <div className="form-unit">
                            <label className="textbox-label">VMI Group</label>
                            <SelectBox id="vmiGroupId" className="selectbox"
                                placeholder='vmi group'
                                displayExpr='name' valueExpr='id'
                                dataSource={assetData?.vmiGroups ?? []}
                                onValueChanged={onNewVmiGroupChanged}
                                value={newVmiGroup ? newVmiGroup.id : null} >
                                <RequiredRule message="VMI Group is required." />
                            </SelectBox>
                        </div>
                    }

                    <div>
                        <Button onClick={onSaveNewAssetClick} text="Save" />
                    </div>

                </DialogPopup>
            }

            {
                assetDocumentLoaderDialogVisible &&
                <DialogPopup visible={assetDocumentLoaderDialogVisible}
                    onHiding={hideAssetDocumentLoaderDialog}
                    title="Asset Documents"
                    width="45rem"
                    height="35rem"
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <DocumentLoader
                        readOnly={false} canDelete={true}
                        parentId={selectedAsset && selectedAsset.assetId ?
                            selectedAsset.assetId : undefined}
                        documentType={DocumentType.Asset} />
                </DialogPopup>
            }

            {(logBookDialogVisible && selectedAsset) &&
                <DialogPopup visible={logBookDialogVisible}
                    onHiding={hideLogBookDialog}
                    title="Asset Logbook"
                    fullScreen={true}>
                    <LogBookEntryComponent asset={selectedAsset ? selectedAsset : null}
                        onClose={hideLogBookDialog} />
                </DialogPopup>
            }

            {
                itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {
                toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={600} />
            }

            {
                globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }
        </div >
    );

    function onShowLogbookDialogClick(e: any) {
        if (e.row && e.row.data)
            setSelectedAsset(e.row.data);

        showLogBookDialog();
    }

    function onEditChildAssetClick(e: any) {
        setIsModifyingChild(true);
        if (e.row && e.row.data) {
            const asset = e.row.data as Asset;
            if (asset) {
                setEditAssetId(asset.assetId);
            }
        }
    }

    function onEditParentAssetClick(e: any) {
        setIsModifyingChild(false);
        // If user is editing a row that is not the currently focued row, this will reset the focus to the row being edited
        const gridInstance = e.component;
        gridInstance.selectRows([e.row.key], false);

        if (e.row && e.row.data) {
            const asset = e.row.data as Asset;

            if (asset) {
                setEditAssetId(asset.assetId);
                setSelectedAsset(asset);
            }
        }
    }

    async function hideAssetEditor() {
        const previouslySelectedAsset = selectedAsset; // this will always be parent
        setAssetEditorVisible(false);

        const freshData = await getAssets();
        const refreshedParent = freshData.find(a => a.assetId === previouslySelectedAsset.assetId);
        if (refreshedParent) {
            setSelectedAsset(refreshedParent);
        } else {
            setSelectedAsset(null);
        }

        setEditAssetId(null);
        setIsModifyingChild(false);
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function clearSelectedRowsInParentGrid() {
        const gridInstance = parentGridRef.current?.instance();
        if (gridInstance) {
            gridInstance.deselectAll();
        } else if (parentGridRef.current?.deselectAll) {
            // Fallback for React versions where instance is mapped directly
            parentGridRef.current.deselectAll();
        }
    }

    // needed?
    function onShowingNewAssetPrompt(e: any) {
        // ??
        //this.validationGroup.instance.reset();
    }

    function onNewAssetNumberChanged(e: any) {
        setNewAssetNumber(e.value);
    }

    function onNewAssetNameChanged(e: any) {
        setNewAssetName(e.value);
    }

    function onNewVmiGroupChanged(e: any) {
        console.log("onNewVmiGroupChanged");
        console.log(e);

        setNewVmiGroup({ id: e.value });
    }

    async function assetGridSelectionChanged(e: any) {
        setIsModifyingChild(false);
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            setSelectedAsset(e.selectedRowsData[0]);
    }

    async function onSaveNewAssetClick(e: any) {
        //todo:
        //let result = e.validationGroup.validate();

        if (!newAssetName || (!isModifyingChild && !newVmiGroup))
            return;

        const payload: Asset = {
            assetName: newAssetName
        };

        if (isModifyingChild) payload.assetParentId = selectedAsset.assetId;
        else payload.vmiGroupId = newVmiGroup.id;

        console.log("Saving asset; payload: ", payload);

        try {
            await CrudFunctions.postData("assets/asset", payload);
            hideNewAssetPrompt();
            const freshData = await getAssets(); // Refresh asset data 
            if (isModifyingChild) {
                // find the parent asset in the fresh data
                const refreshedParent = freshData.find(a => a.assetId === payload.assetParentId);
                // Update the selectedAsset so the child grid reacts
                if (refreshedParent) {
                    setSelectedAsset(refreshedParent);
                }
            } else {
                setSelectedAsset(null);
            }

        } catch (err) {
            showMessage(err, ModalMessageType.Error, "Error Saving Asset");
        } finally {
            setIsModifyingChild(false);
            setNewAssetNumber(null);
            setNewAssetName(null);
            setNewVmiGroup(null);
        }
    }

    function onAssetEditDocuments(e: any) {
        console.log("onAssetEditDocuments");
        console.log(e);

        if (e.row && e.row.data)
            setSelectedAsset(e.row.data);

        showAssetDocumentLoaderDialog();
    }


    function showAssetDocumentLoaderDialog() {
        setAssetDocumentLoaderDialogVisible(true);
    }

    function hideAssetDocumentLoaderDialog() {
        setAssetDocumentLoaderDialogVisible(false);
    }

    function showLogBookDialog() {
        setLogBookDialogVisible(true);
    }

    function hideLogBookDialog() {
        setLogBookDialogVisible(false);
    }

    function onAddNewParentAssetClick() {
        setIsModifyingChild(false);
        setNewAssetNumber(null);
        setNewAssetName(null);
        setNewVmiGroup(null);

        setNewAssetPromptVisible(true);
    }

    function onAddNewChildAssetClick() {
        if (!selectedAsset) return;
        setIsModifyingChild(true);
        setNewAssetNumber(null);
        setNewAssetName(null);
        setNewVmiGroup(null);
        setNewAssetPromptVisible(true);
    }

    function hideNewAssetPrompt() {
        setNewAssetPromptVisible(false);
    }

    // GET functions

    async function getAssets(): Promise<Asset[]> {
        //TODO: just get basic list of assets (incl children)
        //  then in respective components get all the asset details
        setIsLoading(true);
        try {
            const data = await CrudFunctions.getData("assets/allassets");
            setAssets(data);
            return data;
        } catch (err) {
            showMessage(err, ModalMessageType.Error, "Error Retrieving Assets");
            throw err;
        } finally {
            setIsLoading(false);
        }
    }

    async function getAssetData() {
        await CrudFunctions.getData("assets/assetdata")
            .then(data => {
                setAssetData(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Asset Data");
            });
    }

    async function getAllData() {
        await Promise.all([
            getAssets(),
            getAssetData()
        ]);
    }

    //exporting

    function onExporting(e: any) {
        const name = "DMA Audit Log";

        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Main sheet');

        exportDataGrid({
            component: e.component,
            worksheet,
            autoFilterEnabled: true,
        }).then(() => {
            workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${name}.xlsx`);
            });
        });

        e.cancel = true;
    }

    //render


    function assetsDetailsPaneRender(e: any) {
        console.log("assetsDetailsPaneRender");
        console.log(e)

        const asset = e.data.data as Asset;

        return (
            <div className="pw-bg-neutral-050" style={{ padding: "1rem" }}>
                <div>ASSET DETAILS</div>
                <div className="pw-comp-row">
                    <div className="pw-form-unit pw-comp-20">
                        <div className="pw-form-unit-label">Technologies</div>
                        <div className="pw-readonly-label">{asset.technologyList ? asset.technologyList : 'NA'}</div>
                    </div>
                    <div className="pw-form-unit pw-comp-40">
                        <div className="pw-form-unit-label">Description</div>
                        <div className="pw-readonly-label">{asset.description ? asset.description : 'NA'}</div>
                    </div>
                    {/*
                    <div className="pw-form-unit pw-comp-20">
                        <div className="pw-form-unit-label">Date Disposed</div>
                        <div className="pw-readonly-label">{asset.dateDisposed ? PWDates.getDateString(asset.dateDisposed) : 'NA'}</div>
                    </div>
                    */}
                </div>

                <div className="pw-comp-row">
                    <div className="pw-form-unit pw-comp-20">
                        <div className="pw-form-unit-label">Date Decommissioned</div>
                        <div className="pw-readonly-label">{asset.decommissionedDate ? PWDates.getDateString(asset.decommissionedDate) : 'NA'}</div>
                    </div>

                    <div className="pw-form-unit pw-comp-20">
                        <div className="pw-form-unit-label">Zone</div>
                        <div className="pw-readonly-label">{asset.zoneLocation ? asset.zoneLocation : 'NA'}</div>
                    </div>
                    <div className="pw-form-unit pw-comp-20">
                        <div className="pw-form-unit-label">Bin</div>
                        <div className="pw-readonly-label">{asset.binLocation ? asset.binLocation : 'NA'}</div>
                    </div>
                </div>

                {/*
                    <Column dataField='vendorId' visible={false} >
                        <Lookup dataSource={assetData && assetData.vendors ?
                            assetData.vendors : []}
                            allowClearing={true}
                            displayExpr='name' valueExpr='vendorId' />
                    </Column>

                    <Column dataField='contractAgencyId' visible={false} >
                        <Lookup dataSource={assetData && assetData.contractAgencies ?
                            assetData.contractAgencies : []}
                            allowClearing={true}
                            displayExpr='name' valueExpr='contractAgencyId' />
                    </Column>

                    <Column dataField='locationId' visible={false} >
                        <Lookup dataSource={assetData && assetData.locations ?
                            assetData.locations : []}
                            allowClearing={true}
                            displayExpr='name' valueExpr='locationId' />
                    </Column>

                    <Column dataField='departmentId' visible={false} >
                        <Lookup dataSource={assetData && assetData.departments ?
                            assetData.departments : []}
                            allowClearing={true}
                            displayExpr='name' valueExpr='departmentId' />
                    </Column>
                    */}
            </div>
        );
    }


    function renderRevalidationCell(cellData: any) {
        const currentDate = new Date();
        const revalidationDate = cellData.data.revalidationDate;
        const validationRequired = cellData.data.validationRequired;

        if (revalidationDate) {

            if (!validationRequired)
                return (<div className="validation-pass">{PWDates.getDateString(revalidationDate)}</div>);

            const daysDifference = PWDates.getDifferenceInDays(currentDate, new Date(revalidationDate))

            if (daysDifference >= 30)
                return (<div className="validation-pass">{PWDates.getDateString(revalidationDate)}</div>);
            else if (daysDifference < 30 && daysDifference > 0)
                return (<div className="validation-warn">{PWDates.getDateString(revalidationDate)}</div>);
            else
                return (<div className="validation-fail">{PWDates.getDateString(revalidationDate)}</div>);
        }
        else {
            return (<div className="validation-fail">NA</div>);
        }
    }

    function renderDocumentsColumnButton(e: any) {
        if (e.data && e.data.documentCount > 0)
            return <><i className="dx-icon-attach pw-green cursor-pointer"></i><span className="pw-green pw-normal">{e.data.documentCount}</span></>;

        return <i className="dx-icon-attach cursor-pointer"></i>;
    }

    function onAssetsAuditButtonClicked(e: any) {


    }

    function showAssetInformationDialog(e: any) {
        if (e.row && e.row.data) {
            const infoAsset = e.row.data as Asset;

            if (infoAsset) {
                setSelectedAsset(infoAsset);

                let itemInformation: ItemBasicInformation = {
                    rowPointer: infoAsset.assetId,
                    createTimestamp: infoAsset.createTimestamp,
                    lastModifiedTimestamp: infoAsset.modifyTimestamp,
                    createUser: infoAsset.createUser,
                    modifyUser: infoAsset.modifyUser
                };

                setItemBasicInformation(itemInformation);
                setItemBasicInformationDialogVisible(true);
            }
        }
    }

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}
